import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, KeyRound } from "lucide-react";
import { Logo } from "@/components/layout/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type ResetResp = { ok: boolean; message: string; dev_token?: string; dev_reset_url?: string };

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ResetResp | null>(null);
  const [error, setError] = useState<string | null>(null);

  const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "https://api.credcapital.net";

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/auth/password-reset/request`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = (await r.json()) as ResetResp;
      if (!r.ok) throw new Error("request_failed");
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro inesperado");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-muted/40 flex flex-col">
      <header className="bg-background border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center"><Logo /></Link>
          <Link to="/login" className="text-sm text-primary-medium inline-flex items-center gap-1.5">
            <ArrowLeft className="h-3.5 w-3.5" /> Voltar para login
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="h-12 w-12 rounded-md bg-primary-dark text-accent inline-flex items-center justify-center mb-3">
              <KeyRound className="h-5 w-5" />
            </div>
            <h1 className="text-2xl font-display font-bold text-primary-dark">Esqueci a senha</h1>
            <p className="text-sm text-muted-foreground mt-1">Informe seu e-mail e enviaremos instruções para redefinir.</p>
          </div>

          {!result ? (
            <form onSubmit={submit} className="space-y-4 bg-background border border-border rounded-md p-6">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="voce@empresa.com" />
              </div>
              {error && <div className="text-sm text-destructive">{error}</div>}
              <Button type="submit" className="w-full" size="lg" disabled={loading}>
                {loading ? "Enviando..." : "Enviar instruções"}
              </Button>
            </form>
          ) : (
            <div className="bg-background border border-border rounded-md p-6 space-y-4">
              <div className="text-sm text-foreground">{result.message}</div>
              {result.dev_token && (
                <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs">
                  <div className="font-semibold text-amber-900">DEV mode (sem SMTP)</div>
                  <div className="text-amber-800">Em produção, o link viria por email. Para testar agora:</div>
                  <a href={result.dev_reset_url} className="block text-primary-medium underline break-all">{result.dev_reset_url}</a>
                </div>
              )}
              <Link to="/login" className="block text-center text-sm text-primary-medium">Voltar para login</Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
